import { ReactNode, useEffect, useMemo, useState } from "react";
import { Checkbox, Tooltip } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight } from "@fortawesome/free-solid-svg-icons";

import { IReportStructure } from "dto/Reports/IReports";
import {
  ReportNode,
  ReportSeries,
  SeriesValues,
  buildTree,
  flatten,
  userKeysUnder,
} from "./reportModel";
import styles from "./styles.module.scss";

/**
 * Wspólny widok raportu (port GlobalReportVM/ReportsWorkspace z Magistratu):
 * lewa kolumna — drzewo struktury z licznikami i checkboxami (komórka zaznacza
 * poddrzewo), prawa — skumulowany wykres słupkowy zaznaczonych osób (bez biblioteki
 * wykresów, czyste CSS) + tabela wartości. `renderDetails` — panel szczegółów
 * wybranej osoby (raport przeterminowanych).
 */
interface Props {
  structure: IReportStructure | undefined;
  series: ReportSeries[];
  valuesByUser: Map<string, SeriesValues>;
  loading: boolean;
  /** Etykieta kolumny sumy w tabeli (np. „Razem"). */
  totalLabel?: string;
  renderDetails?: (userId: string) => ReactNode;
}

const SCOPE_HINT: Record<string, string> = {
  workspace: "cała firma",
  unit: "moja komórka",
  self: "tylko moje dane",
};

const ReportView = ({
  structure,
  series,
  valuesByUser,
  loading,
  totalLabel = "Razem",
  renderDetails,
}: Props) => {
  const seriesKeys = useMemo(() => series.map((s) => s.key), [series]);

  const roots = useMemo(
    () => (structure ? buildTree(structure, seriesKeys, valuesByUser) : []),
    [structure, seriesKeys, valuesByUser],
  );
  const all = useMemo(() => flatten(roots), [roots]);
  const userNodes = useMemo(() => all.filter((n) => n.isUser), [all]);

  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [selectedUser, setSelectedUser] = useState<string | null>(null);

  // Domyślnie zaznaczone wszystkie osoby (EPM zaznacza węzły „Top").
  useEffect(() => {
    setChecked(new Set(userNodes.map((n) => n.key)));
  }, [userNodes]);

  const toggle = (node: ReportNode) => {
    const keys = userKeysUnder(node);
    const next = new Set(checked);
    const allOn = keys.every((k) => next.has(k));
    keys.forEach((k) => (allOn ? next.delete(k) : next.add(k)));
    setChecked(next);
  };

  const stateOf = (node: ReportNode): "on" | "off" | "mixed" => {
    const keys = userKeysUnder(node);
    if (keys.length === 0) return "off";
    const on = keys.filter((k) => checked.has(k)).length;
    return on === 0 ? "off" : on === keys.length ? "on" : "mixed";
  };

  const selected = useMemo(
    () =>
      userNodes
        .filter((n) => checked.has(n.key))
        .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name, "pl")),
    [userNodes, checked],
  );
  const max = Math.max(1, ...selected.map((n) => n.total));
  const grandTotal = selected.reduce((s, n) => s + n.total, 0);

  const renderNode = (node: ReportNode, depth: number): ReactNode => {
    const st = stateOf(node);
    const isCollapsed = collapsed.has(node.key);
    return (
      <div key={node.key}>
        <div
          className={`${styles.treeRow} ${node.isUser ? styles.treeUser : styles.treeUnit} ${
            selectedUser === node.userId ? styles.treeRowActive : ""
          }`}
          style={{ paddingLeft: 8 + depth * 18 }}
          onClick={() => node.isUser && renderDetails && setSelectedUser(node.userId!)}
        >
          {!node.isUser ? (
            <button
              className={styles.caret}
              onClick={(e) => {
                e.stopPropagation();
                const next = new Set(collapsed);
                if (isCollapsed) next.delete(node.key);
                else next.add(node.key);
                setCollapsed(next);
              }}
            >
              <FontAwesomeIcon icon={isCollapsed ? faChevronRight : faChevronDown} />
            </button>
          ) : (
            <span className={styles.caretSpacer} />
          )}
          <Checkbox
            size="small"
            checked={st === "on"}
            indeterminate={st === "mixed"}
            onClick={(e) => e.stopPropagation()}
            onChange={() => toggle(node)}
          />
          <div className={styles.treeLabel}>
            <div className={styles.treeName}>{node.name}</div>
            {node.subtitle && <div className={styles.treeSub}>{node.subtitle}</div>}
          </div>
          <Tooltip
            title={series.map((s) => `${s.label}: ${node.values[s.key] ?? 0}`).join(" · ")}
            placement="left"
          >
            <div className={styles.treeCount}>{node.total}</div>
          </Tooltip>
        </div>
        {!node.isUser && !isCollapsed && node.children.map((c) => renderNode(c, depth + 1))}
      </div>
    );
  };

  return (
    <div className={styles.body}>
      <div className={styles.tree}>
        <div className={styles.treeHeader}>
          <span>Struktura</span>
          {structure && (
            <span className={styles.scope}>{SCOPE_HINT[structure.Scope] ?? structure.Scope}</span>
          )}
        </div>
        {loading && all.length === 0 ? (
          <div className={styles.empty}>Generowanie raportu…</div>
        ) : all.length === 0 ? (
          <div className={styles.empty}>Brak osób w strukturze.</div>
        ) : (
          roots.map((n) => renderNode(n, 0))
        )}
      </div>

      <div className={styles.main}>
        <div className={styles.legend}>
          {series.map((s) => (
            <span key={s.key} className={styles.legendItem}>
              <i style={{ background: s.color }} />
              {s.label}
            </span>
          ))}
          <span className={styles.legendTotal}>
            Zaznaczono: {selected.length} · {totalLabel.toLowerCase()}: {grandTotal}
          </span>
        </div>

        <div className={styles.chart}>
          {selected.length === 0 ? (
            <div className={styles.empty}>Zaznacz osoby w strukturze, aby zobaczyć wykres.</div>
          ) : (
            selected.map((n) => (
              <div
                key={n.key}
                className={`${styles.barRow} ${selectedUser === n.userId ? styles.barRowActive : ""}`}
                onClick={() => renderDetails && setSelectedUser(n.userId!)}
              >
                <div className={styles.barLabel} title={n.name}>
                  {n.name} <span className={styles.barTotal}>{n.total}</span>
                </div>
                <div className={styles.barTrack}>
                  {series.map((s) => {
                    const v = n.values[s.key] ?? 0;
                    if (!v) return null;
                    return (
                      <Tooltip key={s.key} title={`${s.label}: ${v}`} placement="top">
                        <div
                          className={styles.barSeg}
                          style={{ width: `${(v / max) * 100}%`, background: s.color }}
                        >
                          {v}
                        </div>
                      </Tooltip>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        {renderDetails && selectedUser && (
          <div className={styles.details}>{renderDetails(selectedUser)}</div>
        )}

        {selected.length > 0 && (
          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.left}>Osoba</th>
                  {series.map((s) => (
                    <th key={s.key}>{s.label}</th>
                  ))}
                  <th>{totalLabel}</th>
                </tr>
              </thead>
              <tbody>
                {selected.map((n) => (
                  <tr key={n.key}>
                    <td className={styles.left}>{n.name}</td>
                    {series.map((s) => (
                      <td key={s.key}>{n.values[s.key] ?? 0}</td>
                    ))}
                    <td className={styles.strong}>{n.total}</td>
                  </tr>
                ))}
                <tr className={styles.sumRow}>
                  <td className={styles.left}>Razem</td>
                  {series.map((s) => (
                    <td key={s.key}>{selected.reduce((sum, n) => sum + (n.values[s.key] ?? 0), 0)}</td>
                  ))}
                  <td className={styles.strong}>{grandTotal}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportView;
